import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../../components/Loading";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { DumbbellChart } from "../../../../components/recharts/ReMixCharts/DumbbellChart";
import {
  getConsumptionData,
  getConsumptionGenerationSummaryData,
  getGenerationData,
  getCAGDateWiseMetrics,
} from "../../../../Rest_api/RestApi";
import { ChartType } from "../../../../models/chart_model";
import { MetricChipHead } from "../../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../../icons/MinIcon.svg";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../../components/table/SummaryTable";
import NewFilterContainer from "../../../../components/NewFilterContainer ";
import MainPageContainer from "../../../../components/MainPageContainer";
import SecondaryHeader from "../../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../../components/tooltip";
import { useDispatch, useSelector } from "react-redux";
import { setLoadingFilterName } from "../../../../store/state/FilterLoadingState";
import { AddDatesWithFileName } from "../../../../helpers/helper";
import { useLegendFilter } from "../../../../hooks/useLegendFilter";
import { ExchangeDataTabs } from "../../../Rajasthan/Demand/Data";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { SearchBox } from "../../../../components/Search";

// ─── Shared constants ─────────────────────────────────────────────────────────

export const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Gap Analysis", value: "peak" },
];

const PEAK_INTERVALS = [
  // { label: "Time Slot", key: "time_block", values: ["time_block"] },
  { label: "Date", key: "date", values: ["date"] },
  // { label: "Week", key: "week", values: ["week"] },
  // { label: "Month", key: "month", values: ["month"] },
];

const DUMBBELL_COMPATIBLE_VARS = ["Demand", "Generation", "Peak Demand"];
const GEOGRAPHY_OPTIONS = ["All India", "Region", "State"];
const TOP_N_OPTIONS = [
  { label: "All", value: null as number | null },
  { label: "Top 10", value: 10 },
  { label: "Top 3", value: 3 },
  { label: "Top 5", value: 5 },
];
const REGIONS = [
  "Northern",
  "Western",
  "Southern",
  "Eastern",
  "North-Eastern",
];

const STATE_REGION_MAP: Record<string, string> = {
  Delhi: "Northern",
  Haryana: "Northern",
  "Himachal Pradesh": "Northern",
  "Jammu & Kashmir": "Northern",
  Punjab: "Northern",
  Rajasthan: "Northern",
  "Uttar Pradesh": "Northern",
  Uttarakhand: "Northern",
  Chandigarh: "Northern",
  Chhattisgarh: "Western",
  Goa: "Western",
  Gujarat: "Western",
  "Madhya Pradesh": "Western",
  Maharashtra: "Western",
  "Andhra Pradesh": "Southern",
  Karnataka: "Southern",
  Kerala: "Southern",
  "Tamil Nadu": "Southern",
  Telangana: "Southern",
  Puducherry: "Southern",
  Bihar: "Eastern",
  Jharkhand: "Eastern",
  Odisha: "Eastern",
  "West Bengal": "Eastern",
  Sikkim: "Eastern",
  "Arunachal Pradesh": "North-Eastern",
  Assam: "North-Eastern",
  Manipur: "North-Eastern",
  Meghalaya: "North-Eastern",
  Mizoram: "North-Eastern",
  Nagaland: "North-Eastern",
  Tripura: "North-Eastern",
};

const titleCase = (str: string) =>
  str
    ?.trim()
    .toLowerCase()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.substring(1))
    .join(" ") ?? "Unknown";

function formatDateToYYYYMMDD(date: string) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Peak Gap Analysis
// ─────────────────────────────────────────────────────────────────────────────

interface OverviewPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  // selectedGeography: string;
  // setSelectedGeography: (geo: string) => void;
}

export function OverviewPeakAnalysis({
  // selectedGeography,
  // setSelectedGeography,
  onXAxisModeChange,
}: OverviewPeakAnalysisProps) {
  const dispatch = useDispatch();
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [demandData, setDemandData] = useState<any[]>([]);
  const [generationData, setGenerationData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedGeography, setSelectedGeography] = useState(
    GEOGRAPHY_OPTIONS[2],
  );
  const [dumbbellVariable, setDumbbellVariable] = useState(
    DUMBBELL_COMPATIBLE_VARS[0],
  );
  const [selectedInterval, setSelectedInterval] = useState(PEAK_INTERVALS[0]);
  const [selectedTopN, setSelectedTopN] = useState<number | null>(null);
  const [metricsData, setMetricsData] = useState<any>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Stable primitive date strings — prevents fetchData/fetchMetrics re-running
  // on every Redux update that changes the DefaultDates object reference but not
  // the actual date values. Primitive string comparison avoids false re-fetches.
  const startDateStr = useMemo(() => {
    const d = DefaultDates[activeTab]?.defaultRangeDate?.startDate;
    return d ? formatDateToYYYYMMDD(d) : "";
  }, [DefaultDates, activeTab]);

  const endDateStr = useMemo(() => {
    const d = DefaultDates[activeTab]?.defaultRangeDate?.endDate;
    return d ? formatDateToYYYYMMDD(d) : "";
  }, [DefaultDates, activeTab]);

  // Clear the FilterMenu's loading spinner after any filter change
  useEffect(() => {
    dispatch(setLoadingFilterName(""));
  }, [selectedInterval, dumbbellVariable, selectedGeography, selectedTopN, dispatch]);

  const fetchData = useCallback(async () => {
    if (!startDateStr || !endDateStr) return;
    setLoading(true);
    try {
      const [demRes, genRes] = await Promise.all([
        getConsumptionData(startDateStr, endDateStr),
        getGenerationData(startDateStr, endDateStr),
      ]);
      const dData = (demRes as any[]).map((d) => ({
        ...d,
        state: titleCase(d.state),
      }));
      const gData = (genRes as any[]).map((g) => ({
        ...g,
        state: titleCase(g.state__name || g.state || "Unknown"),
      }));
      setDemandData(dData);
      setGenerationData(gData);
      setStates(
        Array.from(
          new Set([
            ...dData.map((d) => d.state),
            ...gData.map((g) => g.state),
          ]),
        )
          .filter((s) => s !== "Unknown")
          .sort(),
      );
    } catch (error) {
      console.error("Error fetching demand/gen data:", error);
    } finally {
      setLoading(false);
    }
  }, [startDateStr, endDateStr]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchMetrics = useCallback(async () => {
    if (!startDateStr || !endDateStr) return;
    setMetricsLoading(true);
    try {
      const selectedStatesList = (() => {
        if (selectedGeography === "State" && selectedState.length > 0)
          return selectedState.map((s) => s.value);
        if (selectedGeography === "Region" && selectedRegions.length > 0)
          return states.filter((state) =>
            selectedRegions.includes(STATE_REGION_MAP[state] || ""),
          );
        return [];
      })();

      const res = await getCAGDateWiseMetrics(
        [{ startDate: startDateStr, endDate: endDateStr }],
        selectedStatesList,
      );
      setMetricsData(res);
    } catch (err) {
      console.error("Failed to fetch metrics:", err);
    } finally {
      setMetricsLoading(false);
    }
  }, [startDateStr, endDateStr, selectedGeography, selectedRegions, selectedState, states]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const peakDemand =
    metricsData?.demand_metrics?.["Peak Demand (MW)"]?.value ?? null;
  const avgDemand =
    metricsData?.demand_metrics?.["Average Demand (MW)"]?.value ?? null;
  const peakGen =
    metricsData?.generation_metrics?.["Peak Generation (MU)"]?.value ?? null;
  const avgGen =
    metricsData?.generation_metrics?.["Average Generation (MU)"]?.value ?? null;
  const peakOutage =
    metricsData?.outage_metrics?.["Peak Outage (MW)"]?.value ?? null;
  const avgOutage =
    metricsData?.outage_metrics?.["Average Outage (MW)"]?.value ?? null;

  const derivedDumbbellData = useMemo(() => {
    if (loading) return [];

    const getGeoKey = (stateName: string): string | null => {
      if (selectedGeography === "Region")
        return STATE_REGION_MAP[stateName] || null;
      if (selectedGeography === "All India") return "All India";
      return stateName;
    };

    const isSelected = (geoKey: string): boolean => {
      if (selectedGeography === "State" && selectedState.length > 0)
        return selectedState.some((s) => s.value === geoKey);
      if (selectedGeography === "Region" && selectedRegions.length > 0)
        return selectedRegions.includes(geoKey);
      return true;
    };

    const geoDateMap: Record<string, Record<string, number>> = {};
    const accumulate = (geoKey: string, date: string, val: number) => {
      if (!geoDateMap[geoKey]) geoDateMap[geoKey] = {};
      geoDateMap[geoKey][date] = (geoDateMap[geoKey][date] || 0) + val;
    };

    if (dumbbellVariable === "Peak Demand" || dumbbellVariable === "Demand") {
      const suffix =
        dumbbellVariable === "Peak Demand"
          ? "max_demand_met_during_the_day_mw"
          : "energy_met_mu";
      demandData.forEach((d) => {
        const geoKey = getGeoKey(titleCase(d.state));
        if (!geoKey || !isSelected(geoKey)) return;
        accumulate(
          geoKey,
          String(d.date).slice(0, 10),
          Number(d[suffix] || 0),
        );
      });
    } else if (dumbbellVariable === "Generation") {
      generationData.forEach((g) => {
        const geoKey = getGeoKey(titleCase(g.state));
        if (!geoKey || !isSelected(geoKey)) return;
        accumulate(
          geoKey,
          String(g.date).slice(0, 10),
          Number(g.generation_mu_actual || 0),
        );
      });
    }

    // Convert MU → MW for energy-based variables (MW = MU × 1000 / 24h)
    const toMW = (v: number) =>
      dumbbellVariable === "Peak Demand" ? v : v * (1000 / 24);

    const result = Object.entries(geoDateMap)
      .map(([state, byDate]) => {
        const sortedKeys = Object.keys(byDate).sort();
        if (sortedKeys.length === 0) return null;
        const allTimePeakRaw = Math.max(...Object.values(byDate));
        const allTimePeakDate = Object.entries(byDate).reduce(
          (best, [d, v]) => (v > best[1] ? [d, v] : best),
          ["", -Infinity],
        )[0];
        const currentPeakDate = sortedKeys[sortedKeys.length - 1];
        const currentPeakRaw = byDate[currentPeakDate];
        const currentPeak = toMW(currentPeakRaw);
        const allTimePeak = toMW(allTimePeakRaw);
        return {
          state,
          currentPeak,
          currentPeakDate,
          allTimePeak,
          allTimePeakDate,
          gap: allTimePeak - currentPeak,
        };
      })
      .filter(
        (d): d is {
          state: string;
          currentPeak: number;
          currentPeakDate: string;
          allTimePeak: number;
          allTimePeakDate: string;
          gap: number;
        } => d !== null && d.allTimePeak > 0,
      );

    if (selectedGeography === "State" && selectedTopN !== null) {
      return result
        .sort((a, b) => b.allTimePeak - a.allTimePeak)
        .slice(0, selectedTopN)
        .sort((a, b) => a.state.localeCompare(b.state));
    }

    return result.sort((a, b) => a.state.localeCompare(b.state));
  }, [
    loading,
    dumbbellVariable,
    demandData,
    generationData,
    selectedGeography,
    selectedState,
    selectedRegions,
    selectedTopN,
  ]);

  const dumbbellUnit = "MW";

  const geoLabel =
    selectedGeography === "Region" ? "Region" :
    selectedGeography === "All India" ? "Geography" : "State";

  const tableLegendsConfig = useMemo(
    () => [
      {
        dataKey: "currentPeak",
        name: `Current ${dumbbellVariable}`,
        unit: dumbbellUnit,
        type: ChartType.Bar,
        dateKey: "currentPeakDate",
      },
      {
        dataKey: "allTimePeak",
        name: "All Time Peak",
        unit: dumbbellUnit,
        type: ChartType.Bar,
        dateKey: "allTimePeakDate",
      },
      {
        dataKey: "gap",
        name: "Gap",
        unit: dumbbellUnit,
        type: ChartType.Bar,
      },
    ],
    [dumbbellVariable, dumbbellUnit],
  );

  const { visibleLegends: peakVisibleLegends } = useLegendFilter(tableLegendsConfig);

  const tablePar = {
    legends: peakVisibleLegends,
    data: derivedDumbbellData,
    tableData: derivedDumbbellData,
    itemsPerPage: 50,
    extraKeys: ["state"],
    extraLabels: [geoLabel],
    defaultSortKey: "allTimePeak",
    defaultSortDirection: "desc" as const,
    filename: AddDatesWithFileName({
      fileName: "Peak Gap Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((m) => m.label)}
            selectedItems={["Peak Gap Analysis"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Interval"
            items={PEAK_INTERVALS.map((i) => i.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(_, idx) => setSelectedInterval(PEAK_INTERVALS[idx])}
          />
          <FilterMenu
            title="Aggregation"
            items={GEOGRAPHY_OPTIONS}
            selectedItems={[selectedGeography]}
            onSelect={(_, i) => {
              setSelectedGeography(GEOGRAPHY_OPTIONS[i]);
              if (GEOGRAPHY_OPTIONS[i] === "All India") {
                setSelectedState([]);
                setSelectedRegions([]);
              }
            }}
          />
          <FilterMenu
            title="Top N States"
            items={TOP_N_OPTIONS.map((o) => o.label)}
            selectedItems={[
              TOP_N_OPTIONS.find((o) => o.value === selectedTopN)?.label ??
                "All",
            ]}
            onSelect={(_, index) => setSelectedTopN(TOP_N_OPTIONS[index].value)}
            disabled={selectedGeography !== "State"}
          />
          <FilterMenu
            title="Variable"
            items={DUMBBELL_COMPATIBLE_VARS}
            selectedItems={[dumbbellVariable]}
            onSelect={(item) => setDumbbellVariable(item)}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
        <div className="flex flex-wrap gap-4 my-0 justify-center">
          <MetricChipHead
            icon={<PeakIcon width={20} height={19} />}
            title="Demand"
            items={[
              {
                label: "Peak",
                value: metricsLoading ? null : peakDemand,
                unit: "MW",
              },
              {
                label: "Average",
                value: metricsLoading ? null : avgDemand,
                unit: "MW",
              },
            ]}
          />
          <MetricChipHead
            icon={<AvgIcon width={20} height={19} />}
            title="Generation"
            items={[
              {
                label: "Peak",
                value: metricsLoading ? null : peakGen,
                unit: "MU",
              },
              {
                label: "Average",
                value: metricsLoading ? null : avgGen,
                unit: "MU",
              },
            ]}
          />
          <MetricChipHead
            icon={<MinIcon width={20} height={19} />}
            title="Outage"
            items={[
              {
                label: "Peak",
                value: metricsLoading ? null : peakOutage,
                unit: "MW",
              },
              {
                label: "Average",
                value: metricsLoading ? null : avgOutage,
                unit: "MW",
              },
            ]}
          />
        </div>
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Peak Gap Analysis"
              subtitle="Fetching demand and generation data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-100">
                  Peak Gap Analysis ({dumbbellUnit})
                </h2>
                <div className="flex gap-x-6 justify-end items-center">
                  {selectedGeography === "Region" && (
                    <SearchBox
                      options={REGIONS.map((r) => ({ value: r, label: r }))}
                      isMany={true}
                      placeholder="Search Regions"
                      selectedOptions={selectedRegions.map((r) => ({
                        value: r,
                        label: r,
                      }))}
                      onChange={(selected) =>
                        setSelectedRegions(
                          (selected as { value: string }[]).map((s) => s.value),
                        )
                      }
                    />
                  )}
                  {selectedGeography === "State" && (
                    <SearchBox
                      options={states.map((s) => ({ value: s, label: s }))}
                      isMany={true}
                      placeholder="Search States"
                      selectedOptions={selectedState}
                      onChange={(e) => setSelectedState(e as any)}
                    />
                  )}
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(tablePar);
                      else if (dataTab.label === "Summary")
                        downloadSummaryCSV(tablePar);
                      else downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <DumbbellChart
                  data={derivedDumbbellData}
                  currentLabel={`Current ${dumbbellVariable}`}
                  peakLabel="All Time Peak"
                  unit={dumbbellUnit}
                  loading={loading}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note="National Power Portal, Grid Controller of India Limited (Daily PSP Reports)"
              text="Current = last date in range | All Time Peak = max across date range"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary Analysis
// ─────────────────────────────────────────────────────────────────────────────

interface OverviewSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
}

export function OverviewSummaryAnalysis({
  onXAxisModeChange,
}: OverviewSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const SUMMARY_CHART_TYPES = [
    { value: ChartType.Bar, label: "Standard Bar", stackId: undefined },
    { value: ChartType.Line, label: "Line" },
    { value: ChartType.Area, label: "Area" },
  ];

  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(
    SUMMARY_CHART_TYPES[1],
  );
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  const fetchData = useCallback(async () => {
    const dates = DefaultDates[activeTab]?.defaultRangeDate;
    if (!dates?.startDate || !dates?.endDate) return;
    setLoading(true);
    try {
      const res = await getConsumptionGenerationSummaryData(
        formatDateToYYYYMMDD(dates.startDate),
        formatDateToYYYYMMDD(dates.endDate),
        [],
      );
      setRawData(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const processed = rawData.map((item) => ({
      slot: item.slot ?? item.time_block,
      slot_number: item.slot ?? item.time_block,
      slot_with_time:
        item.time_string
          ? `${item.slot ?? item.time_block} (${item.time_string})`
          : String(item.slot ?? item.time_block),
      min_demand: Number(item.min_demand_met_mw ?? item.min_demand ?? 0),
      max_demand: Number(item.max_demand_met_mw ?? item.max_demand ?? 0),
      avg_demand: Number(item.avg_demand_met_mw ?? item.avg_demand ?? 0),
      min_demand_date: item.date_of_min ?? item.min_demand_date ?? "",
      max_demand_date: item.date_of_max ?? item.max_demand_date ?? "",
    }));

    processed.sort((a, b) => Number(a.slot) - Number(b.slot));

    const filtered =
      selectedBlock.length > 0
        ? processed.filter((item) =>
            selectedBlock.map((b) => b.value).includes(item.slot),
          )
        : processed;

    setChartData(filtered);
    setTableData(filtered);
  }, [rawData, selectedBlock]);

  const legends = useMemo(
    () => [
      {
        dataKey: "min_demand",
        name: "Min Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        dateKey: "min_demand_date",
      },
      {
        dataKey: "max_demand",
        name: "Max Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        dateKey: "max_demand_date",
      },
      {
        dataKey: "avg_demand",
        name: "Average Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
      },
    ],
    [selectedChartType],
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const resolveTooltipName = useCallback(
    (seriesName: string, dataPoint: any) => {
      if (seriesName === "Min Demand" && dataPoint?.min_demand_date)
        return `Min Demand (${dataPoint.min_demand_date})`;
      if (seriesName === "Max Demand" && dataPoint?.max_demand_date)
        return `Max Demand (${dataPoint.max_demand_date})`;
      return seriesName;
    },
    [],
  );

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: "MW",
    block_key: "slot_number",
    extraKeys: ["slot_number"],
    extraLabels: ["Block"],
    filename: AddDatesWithFileName({
      fileName: "Overview Summary",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((m) => m.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Chart Type"
            items={SUMMARY_CHART_TYPES.map((m) => m.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, i) => setSelectedChartType(SUMMARY_CHART_TYPES[i])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Summary"
              subtitle="Fetching slot-wise demand summary…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">Overview Summary (MW)</h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(tablePar);
                      else if (dataTab.label === "Summary")
                        downloadSummaryCSV(tablePar);
                      else if (dataTab.label === "Table")
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>
              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedChartType.value}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={true}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit="MW"
                  yAxisLabel="MW"
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey="slot"
                  resolveDisplayName={resolveTooltipName}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note="National Power Portal, Grid Controller of India Limited (Daily PSP Reports)"
              text="Slot-wise min/max/avg across the selected date range"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
